import React, { FC, ReactNode } from "react";
import { IconAlertGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { PollIndicator } from "../../../videos/selectedVideoCardV2/performance/ScoreState";
import { Spinner } from "./ui";

/**
 * The three inline strips a PageBlock draws inside its card (handoff README §3):
 * working (queued / scoring / delayed), alert (a failed attempt) and recessed
 * (Scoring paused). The sections' own empty, loading and error looks are the
 * video details page's ScoreState blocks in a SectionCard (SectionCard.tsx).
 *
 * Usage:
 *   <WorkingStrip title="Report queued" body="Runs in the background — usually a few minutes." poll="Checking every 5 seconds" />
 *   <AlertStrip title="Last scoring attempt failed" body="…" action={retry} />
 *   <RecessedStrip icon={glyph("pause", 16, 1.8)} title="Scoring paused" body="…" />
 *
 * Tokens only; dark is the token swap. Headlines copy the live strip titles'
 * `font-CircularStdBlack` (ScoreState.tsx:81, PanelStates.tsx:47), which
 * renders Book (D2). Inline strips are radius 8 per the handoff.
 */

const HEADLINE_STYLE = { fontSize: 13.5, lineHeight: 1.3 } as const;
const BODY_STYLE = { fontSize: 12.5, lineHeight: 1.4 } as const;

interface WorkingStripProps {
  title: ReactNode;
  body?: ReactNode;
  /** PollIndicator copy: "Checking every 5 seconds" (Product pages' POLL_NOTE). */
  poll?: string;
  /**
   * The counter at the right: "0:42 elapsed", "Queued 22 min ago". Outside the
   * live region. Drawn as the handoff's meta note (06, PageBlock.dc.html:56):
   * a still 6px --accent dot and 11.5px --accent-text, which also reads at
   * 4.5:1 on --tint where --text-2 doesn't (4.07). No pulse: nothing polls in
   * the delayed view, and the live PollIndicator's pulsing --up dot means one.
   * Not tabular as a whole (Circular's tabular spaces open up the words): the
   * caller wraps a ticking number in its own tabular-nums span.
   */
  meta?: ReactNode;
  /** Under the row: ProgressSweep while a sync score runs. */
  footer?: ReactNode;
}

/**
 * Something is running: --tint, spinner, the poll's visible contract.
 *
 * The live region (role="status", README:345) is the title and body only, so a
 * change of state (queued → delayed) is announced. `meta` ticks every second
 * while a sync score runs; it sits outside the region with the poll and the
 * footer, so it is never read aloud. The live page keeps its
 * "N seconds elapsed" line out of any live region too (ScoreNowAction.tsx:196-199).
 */
export const WorkingStrip: FC<WorkingStripProps> = ({ title, body, poll, meta, footer }) => (
  <div className="flex flex-col gap-2.5 rounded-lg bg-tint px-3.5 py-3">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <Spinner />
      <div role="status" className="grow basis-[200px] min-w-0 flex flex-col gap-0.5">
        <div className="font-CircularStdBlack text-text-1" style={HEADLINE_STYLE}>
          {title}
        </div>
        {body && (
          <div className="text-text-2" style={BODY_STYLE}>
            {body}
          </div>
        )}
      </div>
      {meta && (
        <span className="inline-flex items-center gap-1.5 text-accent-text" style={{ fontSize: 11.5 }}>
          <span className="rounded-full bg-accent shrink-0" style={{ width: 6, height: 6 }} aria-hidden="true" />
          {/* One flex item, so "0:03 elapsed" keeps its word space (inline
              children would each become a flex item, split by the gap). */}
          <span>{meta}</span>
        </span>
      )}
      {poll && <PollIndicator>{poll}</PollIndicator>}
    </div>
    {footer}
  </div>
);

interface AlertStripProps {
  title: ReactNode;
  body?: ReactNode;
  /** The on-alert button: btnClass("onAlert"). */
  action?: ReactNode;
  /** Under the row, staff only: MonoError with the raw token. */
  footer?: ReactNode;
}

/** Something failed: --down-bg with --down text and icon. Announced. */
export const AlertStrip: FC<AlertStripProps> = ({ title, body, action, footer }) => (
  <div role="alert" className="flex flex-col gap-2.5 rounded-lg bg-down-bg text-down py-2.5 pr-2.5 pl-3.5">
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
      <span className="inline-flex shrink-0">
        <IconAlertGlyph size={16} strokeWidth={1.8} />
      </span>
      <div className="grow basis-[200px] min-w-0 flex flex-col gap-0.5">
        <div className="font-CircularStdBlack" style={HEADLINE_STYLE}>
          {title}
        </div>
        {body && <div style={BODY_STYLE}>{body}</div>}
      </div>
      {action && <div className="flex flex-wrap items-center gap-2 shrink-0">{action}</div>}
    </div>
    {footer}
  </div>
);

interface RecessedStripProps {
  /** 16px, drawn in --text-2: the pause glyph for Scoring paused. */
  icon?: ReactNode;
  title?: ReactNode;
  body?: ReactNode;
}

/** Nothing to do here, by design: --page fill, a step down from the card. Scoring paused (a page never scored). */
export const RecessedStrip: FC<RecessedStripProps> = ({ icon, title, body }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg bg-page px-3.5 py-2.5">
    {icon && (
      <span className="inline-flex shrink-0 text-text-2" aria-hidden="true">
        {icon}
      </span>
    )}
    <div className="grow basis-[200px] min-w-0 flex flex-col gap-0.5">
      {title && (
        <div className="font-CircularStdBlack text-text-1" style={HEADLINE_STYLE}>
          {title}
        </div>
      )}
      {body && (
        <div className="text-text-2" style={BODY_STYLE}>
          {body}
        </div>
      )}
    </div>
  </div>
);
